import { Component } from '@angular/core';
import { AuthService } from '../../../services/auth.service';

interface TermsSection {
  title: string;
  paragraphs: string[];
  bullets?: string[];
}

@Component({
  selector: 'app-terms-and-conditions',
  templateUrl: './terms-and-conditions.component.html',
  standalone: false,
})
export class TermsAndConditionsComponent {
  constructor(private authService: AuthService) {}

  protected isAuthenticated(): boolean {
    return Boolean(this.authService.getUserGuid());
  }

  content: TermsSection[] = [
    {
      title: 'Last Updated',
      paragraphs: ['July 10, 2026'],
    },
    {
      title: 'Using Kekeno Surveys',
      paragraphs: [
        'Kekeno Surveys provides account-based questionnaire, respondent, invite, completion, reporting, notification, and analytics tools. By accessing or using the service, you agree to these terms. If you do not agree, do not use the service.',
        "If you use Kekeno Surveys for an organisation, school, healthcare provider, business, or other entity, you confirm that you are authorised to use the service and manage data on that entity's behalf.",
      ],
    },
    {
      title: 'Beta Service',
      paragraphs: [
        'Kekeno Surveys is actively being tested and improved. Some features may change, be incomplete, break, be removed, or behave differently while the product evolves.',
        'We welcome feedback, but you should treat the service as early-stage software and check important questionnaire, respondent, invite, schedule, and report details before relying on them.',
      ],
    },
    {
      title: 'Accounts and Security',
      paragraphs: [
        'You are responsible for the accuracy of account information you provide and for all activity under your credentials. Keep your email account, password, two-factor authentication setup, and devices secure.',
        'Account owners and administrators are responsible for managing user access, account switching, roles, invited users, OAuth clients, and any connected systems. Promptly remove access for people who should no longer use an account.',
      ],
    },
    {
      title: 'Questionnaires and Respondent Data',
      paragraphs: [
        'You are responsible for the questionnaires you create, the questions you ask, the respondents you invite, the data you upload, and the way you use questionnaire responses, analytics, reports, alerts, and exports.',
        'Before collecting personal or sensitive information, including health information, NHI numbers, student IDs, ethnicity, dates of birth, mobile numbers, free-text answers, attachments, or custom respondent details, you must make sure you have the required authority, consent, privacy notices, and lawful basis.',
      ],
      bullets: [
        'Do not collect unnecessary sensitive information.',
        'Do not invite respondents unless you are authorised to contact them.',
        'Do not use respondent answers for purposes that are unlawful, misleading, discriminatory, or outside the notice you gave respondents.',
        'Check questionnaire logic, branching, scoring, alert thresholds, translations, and imported question sets before publishing or sending invites.',
      ],
    },
    {
      title: 'No Medical or Professional Advice',
      paragraphs: [
        'Kekeno Surveys is a data collection and workflow platform. It is not a medical device, clinical decision system, emergency service, legal advice service, or professional advice service.',
        'Questionnaire results, scores, alerts, reports, summaries, and analytics may be incomplete, delayed, misconfigured, or wrong if the questionnaire, data, or workflow is incomplete or inaccurate. You are responsible for checking important details and for any decisions, interventions, communications, or actions you take based on the service.',
      ],
    },
    {
      title: 'Invites, Notifications, and Delivery',
      paragraphs: [
        'Kekeno Surveys may support invite links, email or SMS dispatch, scheduled questionnaires, reminders, notifications, unread-notification digest emails, and redirects after completion. You are responsible for checking recipient details, scheduled times, questionnaire availability, and any message content before sending.',
        "We aim to provide reliable delivery integrations, but we cannot guarantee that every email, SMS, notification, redirect, or third-party delivery event will arrive on time or be accepted by the recipient's provider.",
      ],
    },
    {
      title: 'OAuth and Connected Clients',
      paragraphs: [
        'If you create or manage OAuth clients, redirect URLs, or connected systems, you are responsible for using them only for authorised applications, keeping credentials confidential, and removing clients or redirect URLs that are no longer needed.',
        'Do not use OAuth features to mislead users, collect data without authority, bypass access controls, or redirect users to unsafe or unauthorised destinations.',
      ],
    },
    {
      title: 'Acceptable Use',
      paragraphs: [
        'Use Kekeno Surveys respectfully, lawfully, and only for genuine questionnaire and survey workflows. We may suspend or restrict access where misuse, security risk, or unlawful activity is detected.',
      ],
      bullets: [
        'Do not upload unlawful, harmful, abusive, misleading, infringing, or malicious content.',
        'Do not attempt to access another account, respondent record, questionnaire, response, file, or integration without permission.',
        'Do not attack, scan, scrape, overload, reverse engineer, or interfere with the service or its providers.',
        'Do not use the service to send spam, phishing, unwanted messages, or unauthorised bulk communications.',
      ],
    },
    {
      title: 'Data and Privacy',
      paragraphs: [
        'Your use of the service is also subject to our Privacy Policy. You are responsible for ensuring that any data you upload, import, collect, view, export, or share through Kekeno Surveys is handled lawfully and with required permissions.',
      ],
    },
    {
      title: 'Intellectual Property',
      paragraphs: [
        'The Kekeno Surveys platform, software, design, branding, documentation, and related materials remain our property or the property of our licensors.',
        'You retain ownership of questionnaire content, respondent data, response data, imported question sets, files, and other content you create or upload, subject to the rights we need to host, process, secure, support, and operate the service.',
      ],
    },
    {
      title: 'Availability and Changes',
      paragraphs: [
        'We aim to keep Kekeno Surveys useful and reliable, but we cannot promise that it will always be available, error-free, secure from every risk, or fit for every use. Features may be modified, paused, or discontinued as part of normal development and operations.',
        'You are responsible for keeping appropriate records, exports, backups, or alternative workflows where interruption to questionnaires, respondent management, reporting, or communications would matter to your organisation.',
      ],
    },
    {
      title: 'Liability',
      paragraphs: [
        'To the maximum extent permitted by law, Kekeno Surveys is provided as-is and without warranties of any kind. We are not liable for indirect, incidental, special, consequential, punitive, or exemplary loss, or for lost profits, lost data, lost opportunities, or business interruption arising from use of the service.',
        'Nothing in these terms limits rights or remedies that cannot lawfully be excluded.',
      ],
    },
    {
      title: 'Suspension and Deletion',
      paragraphs: [
        'We may suspend or restrict access if we reasonably believe there is misuse, security risk, unlawful activity, account compromise, non-compliance with these terms, or risk to respondents, users, providers, or the service.',
        'You can ask for account or data deletion. We will delete, anonymise, or minimise personal information where practical, subject to legal, security, backup, and service integrity requirements described in the Privacy Policy.',
      ],
    },
    {
      title: 'Changes to These Terms',
      paragraphs: [
        'We may update these terms as Kekeno Surveys changes. The latest version will be posted on this page with the updated date above. Continued use of the service means you accept the latest version.',
      ],
    },
    {
      title: 'Contact',
      paragraphs: ['For terms questions, contact us at privacy@kekeno.co.nz.'],
    },
  ];
}
